import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, ChevronRight, FileSignature, Check, User, Bike, FileText, Camera } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import { Field, Info, LicensePhotoSlot } from "@/components/admin/ContractFormFields";

const EMPTY_CUSTOMER = {
  name: "", surname: "", document_number: "", document_type: "Carta d'Identità",
  birth_date: "", birth_place: "",
  address_street: "", address_city: "", address_zip: "",
  phone: "", email: "", license_number: "",
};

export default function AdminContractNew() {
  const { i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromBookingId = searchParams.get("from_booking");
  const sigRef = useRef(null);

  const [step, setStep] = useState(1); // 1=customer, 2=vehicle+period, 3=signature
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleMode, setVehicleMode] = useState("existing"); // existing|custom
  const [vehicleId, setVehicleId] = useState("");
  const [vehicleCustom, setVehicleCustom] = useState({ model: "", plate: "", vehicle_type: "scooter", displacement: "" });

  // Iter 55 — datetime-local inputs expect **local** time (YYYY-MM-DDTHH:MM);
  // `toISOString()` returns UTC, which caused the default value to show 1-2
  // hours behind the real Rome time. `nowLocalIso(ms)` renders the local
  // wall-clock time correctly (respects the admin's browser timezone —
  // which for the desk in Marciana Marina is always Europe/Rome).
  const nowLocalIso = (offsetMs = 0) => {
    const d = new Date(Date.now() + offsetMs);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  // Same conversion for an ISO string coming from the backend (UTC or with
  // timezone offset) → local wall clock for the datetime-local input.
  const isoToLocalDatetime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
    return nowLocalIso(d.getTime() - Date.now());
  };

  const [period, setPeriod] = useState(() => ({
    start_at: nowLocalIso(),
    end_at: nowLocalIso(86400000),
    daily_rate: 50,
    deposit_amount: 200,
    notes: "",
  }));
  const [addons, setAddons] = useState([]);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const HOME_DELIVERY_PRICE = 50;
  const [submitting, setSubmitting] = useState(false);
  // Iter 55/56 — half-day + gift options. `giftMode`:
  //   "none"       → nothing gifted (default)
  //   "half_only"  → only the half-day is gifted (available only when halfDay=true)
  //   "full"       → the entire rental is gifted (addons + deposit stay untouched)
  const [halfDay, setHalfDay] = useState(false);
  const [giftMode, setGiftMode] = useState("none");
  // Keep gift mode consistent when the admin turns half-day off after
  // selecting "half_only" — otherwise a hidden state would silently persist.
  useEffect(() => {
    if (!halfDay && giftMode === "half_only") setGiftMode("none");
  }, [halfDay, giftMode]);
  // Driver's licence photos (front/back). Required for NEW contracts only;
  // backend stores them as optional strings to preserve back-compat with
  // pre-existing rows. Each holds { url, id } returned by /uploads/customer-document.
  const [licenseFront, setLicenseFront] = useState(null);
  const [licenseBack, setLicenseBack] = useState(null);
  const [uploadingSide, setUploadingSide] = useState(null); // "front" | "back" | null
  // Iter 42 — amount the customer already paid online (comes from
  // /prefill-from-booking). Shown as a green chip and subtracted from the
  // "Da incassare al banco" total on step 2/3.
  const [prepaidOnline, setPrepaidOnline] = useState(0);
  // Iter 47 — how the customer pays the vehicle deposit (cauzione).
  // Displayed on the printed contract PDF; default = contanti.
  const [depositMethod, setDepositMethod] = useState("contanti");

  useEffect(() => {
    api.get("/scooters").then(({ data }) => setVehicles(data || []));
  }, []);

  // Auto-fill the entire form from an existing online booking when
  // /admin/contracts/new?from_booking=<id> is opened.
  useEffect(() => {
    if (!fromBookingId) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/contracts/prefill-from-booking/${fromBookingId}`);
        if (!active) return;
        setCustomer((c) => ({ ...c, ...(data.customer || {}) }));
        if (data.vehicle?.scooter_id) {
          setVehicleMode("existing");
          setVehicleId(data.vehicle.scooter_id);
        } else if (data.vehicle?.model) {
          setVehicleMode("custom");
          setVehicleCustom({
            model: data.vehicle.model || "",
            plate: data.vehicle.plate || "",
            vehicle_type: data.vehicle.vehicle_type || "scooter",
            displacement: data.vehicle.displacement || "",
          });
        }
        setPeriod((p) => ({
          ...p,
          start_at: data.start_at ? isoToLocalDatetime(data.start_at) : p.start_at,
          end_at: data.end_at ? isoToLocalDatetime(data.end_at) : p.end_at,
          daily_rate: data.daily_rate || p.daily_rate,
          deposit_amount: data.deposit_amount || p.deposit_amount,
          notes: data.notes || "",
        }));
        if (Array.isArray(data.addons)) setAddons(data.addons);
        if (data.delivery_address) setDeliveryAddress(data.delivery_address);
        // Iter 42 — carry the "already paid online" amount so the wizard
        // shows the residual to collect (subtotal − prepaid) rather than
        // treating the caparra as a vehicle deposit.
        setPrepaidOnline(Number(data.prepaid_online || 0));
        toast.success(isEN ? "Booking data loaded — customer only needs to sign" : "Dati prenotazione caricati — manca solo la firma del cliente");
      } catch (e) {
        if (active) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { active = false; };
  }, [fromBookingId, isEN]);

  const setCust = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));
  const setPer = (k, v) => setPeriod((p) => ({ ...p, [k]: v }));

  const selectedVehicle = vehicles.find((v) => v.id === vehicleId);

  // Auto-fill daily rate AND deposit when picking existing vehicle — handled
  // imperatively in the Select's onValueChange to keep useEffect side-effect free.
  const pickVehicle = (vid) => {
    setVehicleId(vid);
    const v = vehicles.find((x) => x.id === vid);
    if (v) {
      setPeriod((p) => ({
        ...p,
        daily_rate: v.daily_rate ?? p.daily_rate,
        deposit_amount: v.deposit_amount != null ? v.deposit_amount : p.deposit_amount,
      }));
    }
  };

  const addonsTotal = addons.includes("home_delivery") ? HOME_DELIVERY_PRICE : 0;
  // Iter 55/56 — day math with half-day + selective gift.
  // baseDays  = ceil(diff) → whole days from start_at..end_at
  // days      = baseDays (+0.5 if half-day) → what we PRINT on the contract
  // billable  = baseDays (or 0 if gift=full) plus 0.5 unless gift=half_only or gift=full
  const baseDays = (() => {
    if (!period.start_at || !period.end_at) return 1;
    const ms = new Date(period.end_at) - new Date(period.start_at);
    return Math.max(1, Math.ceil(ms / 86400000));
  })();
  const days = halfDay ? +(baseDays + 0.5).toFixed(2) : baseDays;
  const daily = Number(period.daily_rate || 0);
  const rentalSubtotal = (() => {
    if (giftMode === "full") return 0;
    let billable = baseDays;
    if (halfDay && giftMode !== "half_only") billable += 0.5;
    return +(billable * daily).toFixed(2);
  })();
  const grossTotal = +(rentalSubtotal + addonsTotal).toFixed(2);
  const isGift = giftMode === "full";  // legacy compat for downstream code
  // Iter 42 — subtract what was already paid online. `total` is the amount
  // the admin must collect at the desk. Contracts stored to the DB use this
  // residual as `total_amount` so the dashboard and PDFs reflect real cashflow.
  const total = Math.max(0, +(grossTotal - Number(prepaidOnline || 0)).toFixed(2));
  const toggleAddon = (aid) =>
    setAddons((cur) => (cur.includes(aid) ? cur.filter((x) => x !== aid) : [...cur, aid]));

  // Upload one side of the driver's licence and save the returned URL.
  // We use the existing /uploads/customer-document endpoint (admin is also
  // authenticated as user → allowed). Backend stores file in object storage.
  const uploadLicensePhoto = async (file, side) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(isEN ? "File too large (max 8MB)" : "File troppo grande (max 8MB)");
      return;
    }
    setUploadingSide(side);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/customer-document`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      if (side === "front") setLicenseFront({ url: data.url, id: data.id });
      else setLicenseBack({ url: data.url, id: data.id });
      toast.success(isEN ? "Photo uploaded" : "Foto caricata");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploadingSide(null);
    }
  };

  const validateStep1 = () => {
    const req = ["name", "surname", "document_number", "birth_date", "birth_place", "address_street", "address_city", "address_zip", "phone"];
    const missing = req.filter((k) => !String(customer[k] || "").trim());
    if (missing.length) {
      toast.error(isEN ? `Missing: ${missing.join(", ")}` : `Mancano: ${missing.join(", ")}`);
      return false;
    }
    // License photos are required for NEW contracts (skip when loaded from
    // a pre-existing online booking — those rows might predate this feature).
    if (!licenseFront?.url || !licenseBack?.url) {
      toast.error(isEN
        ? "Photo of driver's licence (front + back) required"
        : "Foto patente fronte/retro obbligatorie");
      return false;
    }
    return true;
  };
  const validateStep2 = () => {
    if (vehicleMode === "existing" && !vehicleId) {
      toast.error(isEN ? "Pick a vehicle" : "Seleziona un veicolo");
      return false;
    }
    if (vehicleMode === "custom" && (!vehicleCustom.model || !vehicleCustom.plate)) {
      toast.error(isEN ? "Model and plate required" : "Modello e targa obbligatori");
      return false;
    }
    if (!period.daily_rate || period.daily_rate <= 0) {
      toast.error(isEN ? "Daily rate required" : "Tariffa giornaliera obbligatoria");
      return false;
    }
    if (addons.includes("home_delivery") && deliveryAddress.trim().length < 5) {
      toast.error(isEN
        ? "Delivery address (or hotel name) required for home delivery"
        : "Indica l'indirizzo o il nome dell'hotel per la consegna a domicilio");
      return false;
    }
    return true;
  };

  const next = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep((s) => s + 1);
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (sigRef.current?.isEmpty()) {
      toast.error(isEN ? "Signature required" : "Firma obbligatoria");
      return;
    }
    setSubmitting(true);
    try {
      const sigData = sigRef.current.toDataURL();
      const vehiclePayload = vehicleMode === "existing"
        ? {
            scooter_id: selectedVehicle.id,
            model: selectedVehicle.model,
            plate: selectedVehicle.plate,
            vehicle_type: selectedVehicle.vehicle_type || "scooter",
            displacement: selectedVehicle.displacement,
          }
        : {
            ...vehicleCustom,
            displacement: vehicleCustom.displacement ? parseInt(vehicleCustom.displacement, 10) : null,
          };
      const payload = {
        customer: {
          ...customer,
          license_front_url: licenseFront?.url || null,
          license_back_url: licenseBack?.url || null,
        },
        vehicle: vehiclePayload,
        start_at: new Date(period.start_at).toISOString(),
        end_at: new Date(period.end_at).toISOString(),
        daily_rate: Number(period.daily_rate),
        days,
        // Iter 47 — store the FULL rental total (sede + online). The PDF
        // "TOTALE" line shows this; a separate "di cui pagato online" line
        // clarifies the desk cashflow.
        total_amount: grossTotal,
        prepaid_online: Number(prepaidOnline || 0),
        deposit_amount: Number(period.deposit_amount || 0),
        deposit_payment_method: depositMethod,
        addons,
        addons_total: addonsTotal,
        delivery_address: addons.includes("home_delivery") ? deliveryAddress.trim() : null,
        // Iter 55/56 — auto-append notes describing half-day / gift, and send
        // the granular flags so the backend/PDF renders them correctly.
        notes: (() => {
          const extras = [];
          if (halfDay) extras.push(isEN ? "Half day added (+0.5)" : "Mezza giornata aggiunta (+0.5)");
          if (giftMode === "half_only") extras.push(isEN ? "GIFT — half day only" : "OMAGGIO — solo la mezza giornata");
          else if (giftMode === "full") extras.push(isEN ? "GIFT — no rental fee" : "OMAGGIO — noleggio gratuito");
          const base = (period.notes || "").trim();
          return [base, extras.join(" · ")].filter(Boolean).join("\n") || null;
        })(),
        half_day: halfDay,
        gift_mode: giftMode,
        is_gift: giftMode === "full",  // legacy flag kept for older readers
        signature_data_url: sigData,
        source_booking_id: fromBookingId || null,
      };
      const { data } = await api.post("/admin/contracts", payload);
      toast.success(isEN ? "Contract saved" : "Contratto salvato");
      navigate(`/admin/contracts`);
      // Auto-download PDF
      setTimeout(async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contracts/${data.id}/pdf`, {
            headers: authHeaders(),
          });
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `contratto_${data.customer.surname}_${data.id.slice(0,8)}.pdf`;
          a.click();
          URL.revokeObjectURL(url);
        } catch (_) { /* noop */ }
      }, 500);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-contract-new-page">
      <Button variant="ghost" onClick={() => navigate("/admin/contracts")} className="text-zinc-400 hover:text-zinc-100" data-testid="contract-new-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> {isEN ? "Back to contracts" : "Torna ai contratti"}
      </Button>

      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700]">{isEN ? "Walk-in contract" : "Contratto in sede"}</p>
        <h1 className="font-display text-4xl font-bold mt-1">{isEN ? "New manual contract" : "Nuovo contratto manuale"}</h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-sm">
        {[
          { n: 1, label: isEN ? "Customer" : "Cliente", icon: User },
          { n: 2, label: isEN ? "Vehicle & price" : "Veicolo e prezzo", icon: Bike },
          { n: 3, label: isEN ? "Signature" : "Firma", icon: FileSignature },
        ].map((s, i) => {
          const Icon = s.icon;
          const active = step === s.n;
          const done = step > s.n;
          return (
            <div key={s.n} className="flex items-center gap-2">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
                active ? "bg-[#FFD700] text-zinc-950 font-semibold"
                : done ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                : "bg-zinc-900 text-zinc-500 border border-zinc-800"}`}>
                {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                <span className="text-xs">{s.label}</span>
              </div>
              {i < 2 && <ChevronRight className="w-4 h-4 text-zinc-700" />}
            </div>
          );
        })}
      </div>

      {/* Step content */}
      <div className="border border-zinc-800 rounded-2xl bg-zinc-900/40 p-6">
        {step === 1 && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="step-customer">
            <Field label={isEN ? "First name *" : "Nome *"} value={customer.name} onChange={(v) => setCust("name", v)} testId="cust-name" />
            <Field label={isEN ? "Last name *" : "Cognome *"} value={customer.surname} onChange={(v) => setCust("surname", v)} testId="cust-surname" />
            <Field label={isEN ? "Document number *" : "N. Documento *"} value={customer.document_number} onChange={(v) => setCust("document_number", v.toUpperCase())} testId="cust-doc" />
            <div>
              <Label className="text-xs text-zinc-400">{isEN ? "Document type" : "Tipo documento"}</Label>
              <Select value={customer.document_type} onValueChange={(v) => setCust("document_type", v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="cust-doc-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="Carta d'Identità">Carta d&apos;Identità</SelectItem>
                  <SelectItem value="Passaporto">Passaporto</SelectItem>
                  <SelectItem value="Patente">Patente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label={isEN ? "Date of birth *" : "Data di nascita *"} type="date" value={customer.birth_date} onChange={(v) => setCust("birth_date", v)} testId="cust-birth-date" />
            <Field label={isEN ? "Place of birth *" : "Luogo di nascita *"} value={customer.birth_place} onChange={(v) => setCust("birth_place", v)} testId="cust-birth-place" />
            <Field label={isEN ? "Phone *" : "Telefono *"} type="tel" value={customer.phone} onChange={(v) => setCust("phone", v)} testId="cust-phone" />
            <Field label="Email" type="email" value={customer.email} onChange={(v) => setCust("email", v)} testId="cust-email" />
            <Field label={isEN ? "Driving licence" : "Patente"} value={customer.license_number} onChange={(v) => setCust("license_number", v.toUpperCase())} testId="cust-licence" />
            <div className="sm:col-span-2">
              <Field label={isEN ? "Street and number *" : "Via e numero civico *"} value={customer.address_street} onChange={(v) => setCust("address_street", v)} testId="cust-street" />
            </div>
            <Field label={isEN ? "City *" : "Città *"} value={customer.address_city} onChange={(v) => setCust("address_city", v)} testId="cust-city" />
            <Field label={isEN ? "ZIP *" : "CAP *"} value={customer.address_zip} onChange={(v) => setCust("address_zip", v)} testId="cust-zip" />

            <div className="sm:col-span-2 border border-[#FFD700]/30 rounded-xl p-4 bg-[#FFD700]/[0.04] space-y-3" data-testid="license-photo-block">
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-[#FFD700]" />
                <span className="text-xs uppercase tracking-widest text-[#FFD700] font-semibold">
                  {isEN ? "Driver's licence — photos *" : "Foto patente di guida *"}
                </span>
              </div>
              <p className="text-xs text-zinc-400">
                {isEN
                  ? "Take a picture of the customer's driving licence (front and back). Stored permanently in the customer dossier."
                  : "Scatta una foto della patente del cliente (fronte e retro). Archiviata permanentemente nel fascicolo cliente."}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <LicensePhotoSlot
                  side="front"
                  label={isEN ? "Front" : "Fronte"}
                  photo={licenseFront}
                  uploading={uploadingSide === "front"}
                  onPick={(f) => uploadLicensePhoto(f, "front")}
                  onClear={() => setLicenseFront(null)}
                  isEN={isEN}
                />
                <LicensePhotoSlot
                  side="back"
                  label={isEN ? "Back" : "Retro"}
                  photo={licenseBack}
                  uploading={uploadingSide === "back"}
                  onPick={(f) => uploadLicensePhoto(f, "back")}
                  onClear={() => setLicenseBack(null)}
                  isEN={isEN}
                />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="step-vehicle">
            <div className="flex gap-2">
              <Button type="button" variant={vehicleMode === "existing" ? "default" : "outline"}
                      onClick={() => setVehicleMode("existing")}
                      className={vehicleMode === "existing" ? "bg-[#FFD700] text-zinc-950" : "bg-zinc-950 border-zinc-700"}
                      data-testid="veh-mode-existing">
                {isEN ? "From fleet" : "Dalla flotta"}
              </Button>
              <Button type="button" variant={vehicleMode === "custom" ? "default" : "outline"}
                      onClick={() => setVehicleMode("custom")}
                      className={vehicleMode === "custom" ? "bg-[#FFD700] text-zinc-950" : "bg-zinc-950 border-zinc-700"}
                      data-testid="veh-mode-custom">
                {isEN ? "Custom (external)" : "Personalizzato"}
              </Button>
            </div>

            {vehicleMode === "existing" ? (
              <div>
                <Label className="text-xs text-zinc-400">{isEN ? "Vehicle" : "Veicolo"} *</Label>
                <Select value={vehicleId} onValueChange={pickVehicle}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="veh-select">
                    <SelectValue placeholder={isEN ? "Choose a vehicle…" : "Scegli un veicolo…"} />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 max-h-64 overflow-y-auto">
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {(v.vehicle_type === "auto" ? "🚗 " : "🛵 ")}{v.model} · {v.plate} · €{(v.daily_rate || 0).toFixed(2)}/g
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label={isEN ? "Model *" : "Modello *"} value={vehicleCustom.model} onChange={(v) => setVehicleCustom((p) => ({ ...p, model: v }))} testId="veh-custom-model" />
                <Field label={isEN ? "Plate *" : "Targa *"} value={vehicleCustom.plate} onChange={(v) => setVehicleCustom((p) => ({ ...p, plate: v.toUpperCase() }))} testId="veh-custom-plate" />
                <div>
                  <Label className="text-xs text-zinc-400">{isEN ? "Type" : "Tipo"}</Label>
                  <Select value={vehicleCustom.vehicle_type} onValueChange={(v) => setVehicleCustom((p) => ({ ...p, vehicle_type: v }))}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="veh-custom-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="scooter">🛵 Scooter</SelectItem>
                      <SelectItem value="auto">🚗 Auto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Field label={isEN ? "Displacement (cc)" : "Cilindrata (cc)"} type="number" value={vehicleCustom.displacement} onChange={(v) => setVehicleCustom((p) => ({ ...p, displacement: v }))} testId="veh-custom-cc" />
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-zinc-800">
              <Field label={isEN ? "Start (date & time) *" : "Inizio (data e ora) *"} type="datetime-local" value={period.start_at} onChange={(v) => setPer("start_at", v)} testId="period-start" />
              <Field label={isEN ? "End (date & time) *" : "Fine (data e ora) *"} type="datetime-local" value={period.end_at} onChange={(v) => setPer("end_at", v)} testId="period-end" />
              <Field label={isEN ? "Daily rate (€) *" : "Tariffa €/giorno *"} type="number" value={period.daily_rate} onChange={(v) => setPer("daily_rate", v)} testId="period-rate" />
              <Field label={isEN ? "Deposit (€)" : "Cauzione (€)"} type="number" value={period.deposit_amount} onChange={(v) => setPer("deposit_amount", v)} testId="period-deposit" />
              <div>
                <Label className="text-xs text-zinc-400">{isEN ? "Deposit paid by *" : "Metodo cauzione *"}</Label>
                <Select value={depositMethod} onValueChange={setDepositMethod}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="period-deposit-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="contanti">{isEN ? "Cash" : "Contanti"}</SelectItem>
                    <SelectItem value="carta">{isEN ? "Card (pre-auth)" : "Carta (pre-autorizzazione)"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Iter 55/56 — Half-day + Gift options */}
              <div className="sm:col-span-2 space-y-3">
                <label className="flex items-start gap-2 cursor-pointer rounded-md border border-zinc-800 bg-zinc-950 p-3 hover:border-[#FFD700]/40 transition"
                       data-testid="toggle-half-day-label">
                  <input type="checkbox" checked={halfDay}
                         onChange={(e) => setHalfDay(e.target.checked)}
                         className="mt-0.5 accent-[#FFD700]"
                         data-testid="toggle-half-day" />
                  <div className="flex-1">
                    <div className="text-sm text-zinc-100 font-medium">
                      {isEN ? "Add half day (+0.5)" : "Aggiungi mezza giornata (+0,5)"}
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {isEN
                        ? "Adds €" + (Number(period.daily_rate || 0) / 2).toFixed(2) + " on top of full days"
                        : "Aggiunge €" + (Number(period.daily_rate || 0) / 2).toFixed(2) + " oltre alle giornate piene"}
                    </div>
                  </div>
                </label>

                <div>
                  <Label className="text-xs text-zinc-400">
                    {isEN ? "Gift option" : "Omaggio"}
                  </Label>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="gift-mode-group">
                    {[
                      { value: "none",      label_it: "Nessuno",                 label_en: "None",              hint_it: "Cliente paga normalmente",         hint_en: "Customer pays in full" },
                      { value: "half_only", label_it: "🎁 Solo mezza giornata",  label_en: "🎁 Half day only",  hint_it: "Regala i €" + (Number(period.daily_rate || 0) / 2).toFixed(2) + " della mezza",  hint_en: "Gift the €" + (Number(period.daily_rate || 0) / 2).toFixed(2) + " half day",  disabled: !halfDay },
                      { value: "full",      label_it: "🎁 Tutto il noleggio",    label_en: "🎁 Whole rental",   hint_it: "Totale noleggio a €0",              hint_en: "Rental total goes to €0" },
                    ].map((opt) => {
                      const active = giftMode === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={opt.disabled}
                          onClick={() => setGiftMode(opt.value)}
                          data-testid={`gift-mode-${opt.value}`}
                          className={`text-left rounded-md border p-3 transition ${
                            opt.disabled
                              ? "border-zinc-800 bg-zinc-900/40 opacity-40 cursor-not-allowed"
                              : active
                                ? "border-emerald-500/60 bg-emerald-500/10"
                                : "border-zinc-800 bg-zinc-950 hover:border-emerald-500/40"
                          }`}
                        >
                          <div className="text-sm text-zinc-100 font-medium">
                            {isEN ? opt.label_en : opt.label_it}
                          </div>
                          <div className="text-[11px] text-zinc-500 mt-0.5">
                            {isEN ? opt.hint_en : opt.hint_it}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {!halfDay && (
                    <p className="text-[10px] text-zinc-500 mt-1">
                      {isEN
                        ? 'Enable "half day" above to allow gifting only the half day.'
                        : 'Attiva "mezza giornata" sopra per poter regalare solo la mezza.'}
                    </p>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-zinc-400">{isEN ? "Notes / special conditions" : "Note / condizioni particolari"}</Label>
                <Textarea value={period.notes} onChange={(e) => setPer("notes", e.target.value)}
                          className="bg-zinc-950 border-zinc-800 mt-1" rows={3} data-testid="period-notes" />
              </div>
            </div>

            <div className="border border-[#FFD700]/30 rounded-xl p-4 bg-[#FFD700]/[0.04] space-y-3" data-testid="manual-addons-block">
              <div className="text-xs uppercase tracking-widest text-[#FFD700] font-semibold">
                {isEN ? "Add-ons" : "Bundle e accessori"}
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={addons.includes("home_delivery")}
                       onChange={() => toggleAddon("home_delivery")}
                       className="mt-1 accent-[#FFD700]"
                       data-testid="manual-addon-home_delivery" />
                <div className="flex-1">
                  <div className="text-sm text-zinc-100">
                    {isEN ? "Home delivery" : "Consegna a domicilio"}
                  </div>
                  <div className="text-xs text-zinc-400">
                    {isEN
                      ? "I'm delivering the vehicle to the customer's location (hotel or address). Returned to office for damage check."
                      : "Sto consegnando il veicolo all'indirizzo del cliente (hotel o casa). Restituzione in sede per verifica danni."}
                  </div>
                </div>
                <div className="text-sm font-semibold text-[#FFD700]">+€{HOME_DELIVERY_PRICE.toFixed(2)}</div>
              </label>
              {addons.includes("home_delivery") && (
                <div className="pt-3 border-t border-[#FFD700]/20 space-y-2">
                  <Label className="text-xs text-zinc-300 uppercase tracking-widest">
                    {isEN ? "Delivery address / hotel name" : "Indirizzo di consegna / nome hotel"} <span className="text-[#FFD700]">*</span>
                  </Label>
                  <Input
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder={isEN ? "e.g. Hotel La Conchiglia – Marciana Marina" : "es. Hotel La Conchiglia – Marciana Marina"}
                    className="bg-zinc-950 border-zinc-800"
                    minLength={5}
                    data-testid="manual-delivery-address-input"
                  />
                </div>
              )}
            </div>

            <div className={`rounded-xl border p-4 flex items-center justify-between ${giftMode === "full" ? "bg-emerald-500/10 border-emerald-500/40" : "bg-[#FFD700]/10 border-[#FFD700]/30"}`}>
              <div className="text-sm text-zinc-300 leading-tight">
                {giftMode === "full" ? (
                  <div className="text-emerald-300 font-semibold">
                    {isEN ? "🎁 GIFT — no rental fee" : "🎁 OMAGGIO — noleggio a €0"}
                  </div>
                ) : (
                  <>
                    <div>{days} {isEN ? "days" : "giorni"} × €{daily.toFixed(2)} = €{rentalSubtotal.toFixed(2)}</div>
                    {giftMode === "half_only" && (
                      <div className="text-xs text-emerald-300 font-semibold">
                        {isEN
                          ? `🎁 Half day gifted (−€${(daily / 2).toFixed(2)})`
                          : `🎁 Mezza giornata regalata (−€${(daily / 2).toFixed(2)})`}
                      </div>
                    )}
                  </>
                )}
                {addonsTotal > 0 && (
                  <div className="text-xs text-zinc-400">+ {isEN ? "add-ons" : "accessori"}: €{addonsTotal.toFixed(2)}</div>
                )}
                {prepaidOnline > 0 && (
                  <div className="text-xs text-emerald-300 font-semibold" data-testid="prepaid-online-line">
                    − {isEN ? "Paid online" : "Pagato online"}: €{Number(prepaidOnline).toFixed(2)}
                  </div>
                )}
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
                  {prepaidOnline > 0 ? (isEN ? "To collect at desk" : "Da incassare al banco") : (isEN ? "Total" : "Totale")}
                </div>
              </div>
              <span className={`font-display text-2xl font-bold ${giftMode === "full" ? "text-emerald-300" : "text-[#FFD700]"}`} data-testid="contract-total">€{total.toFixed(2)}</span>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="step-signature">
            <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4 text-sm text-zinc-300 leading-relaxed">
              <FileText className="w-4 h-4 inline mr-1 text-[#FFD700]" />
              {isEN
                ? <>The customer <b>{customer.name} {customer.surname}</b> declares to have read, understood and fully accepted the rental contract of Rent Lupi for the vehicle indicated above.</>
                : <>Il cliente <b>{customer.name} {customer.surname}</b> dichiara di aver letto, compreso e accettato integralmente il contratto di locazione di Rent Lupi per il veicolo indicato sopra.</>}
            </div>

            <div>
              <Label className="text-zinc-300 mb-2 block">{isEN ? "Sign with your finger or stylus on the tablet" : "Firma con il dito o il pennino sul tablet"} *</Label>
              <SignaturePad ref={sigRef} height={240} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <Info label={isEN ? "Customer" : "Cliente"} value={`${customer.name} ${customer.surname}`} />
              <Info label={isEN ? "Document" : "Documento"} value={customer.document_number} mono />
              <Info label={isEN ? "Vehicle" : "Veicolo"} value={vehicleMode === "existing" ? `${selectedVehicle?.model} · ${selectedVehicle?.plate}` : `${vehicleCustom.model} · ${vehicleCustom.plate}`} />
              <Info
                label={prepaidOnline > 0 ? (isEN ? "To collect" : "Da incassare") : "Totale"}
                value={`€${total.toFixed(2)}`}
              />
              {prepaidOnline > 0 && (
                <div className="col-span-2 sm:col-span-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3" data-testid="prepaid-online-info">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-300">
                    {isEN ? "Already paid online" : "Già pagato online"}
                  </p>
                  <p className="text-emerald-200 text-sm mt-0.5">
                    €{Number(prepaidOnline).toFixed(2)} — {isEN
                      ? `original total €${grossTotal.toFixed(2)}, deducted from what the customer owes at the desk.`
                      : `totale originale €${grossTotal.toFixed(2)}, detratto da quanto il cliente deve pagare al banco.`}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={back} disabled={step === 1} className="text-zinc-400" data-testid="step-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> {isEN ? "Back" : "Indietro"}
        </Button>
        {step < 3 ? (
          <Button onClick={next} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="step-next">
            {isEN ? "Continue" : "Continua"} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={submit} disabled={submitting} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="contract-submit">
            <FileSignature className="w-4 h-4 mr-1" />
            {submitting ? (isEN ? "Saving…" : "Salvataggio…") : (isEN ? "Save & download PDF" : "Salva e scarica PDF")}
          </Button>
        )}
      </div>
    </div>
  );
}


